/**
 * Browser support gate for HIMT Video Assessment for Additional Screening.
 *
 * Screen-recording proctoring depends on `navigator.mediaDevices.getDisplayMedia()`
 * + `displaySurface: "monitor"` pre-selection. Only Chromium-based browsers
 * (Chrome, Edge, Brave, Arc, Opera) implement this reliably on desktop.
 *
 * Safari's getDisplayMedia is flaky and rejects Chrome-specific constraints;
 * Firefox doesn't honor displaySurface preselect and Android/iOS mobile
 * browsers lack screen-capture entirely. We block all of them at the gate
 * with a friendly "please use Chrome/Edge" message + install link — same
 * approach as Proctorio / Examity / ProctorU.
 */

export function detectBrowser() {
  if (typeof navigator === "undefined") {
    return { name: "unknown", isSupported: false, isMobile: false, reason: "server-side" };
  }
  const ua = navigator.userAgent || "";
  const uaLower = ua.toLowerCase();

  // Mobile detection — mobile browsers lack getDisplayMedia.
  const isMobile = /android|iphone|ipad|ipod|mobile|tablet/.test(uaLower);

  // Brand detection order matters — Edge/Opera/Brave all contain "Chrome"
  // in UA, so check them first.
  let name = "unknown";
  if (/edg\//i.test(ua)) name = "edge";
  else if (/opr\/|opera/i.test(ua)) name = "opera";
  else if (/brave/i.test(ua) || (navigator.brave && typeof navigator.brave.isBrave === "function")) name = "brave";
  else if (/chrome\//i.test(ua) && !/edg\//i.test(ua)) name = "chrome";
  else if (/firefox\//i.test(ua)) name = "firefox";
  else if (/safari\//i.test(ua) && !/chrome\//i.test(ua) && !/crios\//i.test(ua)) name = "safari";

  const hasDisplayMedia =
    typeof navigator.mediaDevices !== "undefined" &&
    typeof navigator.mediaDevices.getDisplayMedia === "function";

  const hasUserMedia =
    typeof navigator.mediaDevices !== "undefined" &&
    typeof navigator.mediaDevices.getUserMedia === "function";

  // Supported list — Chromium browsers on desktop.
  const chromiumDesktop = ["chrome", "edge", "opera", "brave"].includes(name);
  const isSupported = !isMobile && chromiumDesktop && hasDisplayMedia && hasUserMedia;

  let reason = null;
  if (isMobile) reason = "mobile";
  else if (name === "safari") reason = "safari";
  else if (name === "firefox") reason = "firefox";
  else if (!hasDisplayMedia) reason = "no-display-media";
  else if (!hasUserMedia) reason = "no-user-media";
  else if (!chromiumDesktop) reason = "unknown-browser";

  return { name, isSupported, isMobile, hasDisplayMedia, hasUserMedia, reason };
}

export const BROWSER_BLOCK_COPY = {
  title: "Unsupported browser",
  message:
    "This secure oral assessment can only be taken on Google Chrome or Microsoft Edge on a desktop or laptop. Safari, Firefox, and mobile browsers do not meet the proctoring requirements.",
  actions: [
    { label: "Download Google Chrome", href: "https://www.google.com/chrome/" },
    { label: "Download Microsoft Edge", href: "https://www.microsoft.com/edge" },
  ],
  help:
    "After installing Chrome or Edge, copy your exam link and paste it into the new browser. Your invite is still valid.",
};
